import template from './bk-modal-template';
import { dispatchStopLoadingEvent } from 'Base/modules/bk/events/bk-loading-animation';
import './styles.scss';
import { t } from 'Base/modules/bk/component-property-providers/bk-translations';

class BkModal extends HTMLElement {
    static CLOSE_TYPE_X = 'BkModalCloseTypeX';
    static CLOSE_TYPE_PRIMARY_BUTTON = 'BkModalCloseTypePrimaryButton';
    static CLOSE_TYPE_SECONDARY_BUTTON = 'BkModalCloseTypeSecondaryButton';

    constructor() {
        super();
    }

    connectedCallback() {
        const content = template.getTemplate().content.cloneNode(true);
        this.append(content);
        this.querySelector('[data-modal-close]').addEventListener('click', () => {
            this.closeModal(BkModal.CLOSE_TYPE_X);
        });
    }

    #setDefaultValues(params) {
        params.showSecondaryButton = params.showSecondaryButton ?? true;
        params.cancelText = params.cancelText || t('global.cancel');
        params.submitText = params.submitText || t('global.ok');
    }

    reset() {
        const modalBody = this.querySelector('[data-modal-body]');
        if (modalBody) {
            modalBody.innerHTML = '';
        }
        this.querySelector('header h2')?.remove();
        this.querySelector('footer')?.remove();
    }

    #getModal() {
        this.reset();
        const modal = this.querySelector('[data-page-modal]');
        if (!modal) {
            throw new Error('modal not found');
        }

        return modal;
    }

    /**
     * opens the modal with content based on params
     * @param {Object} params - object with the modal's configuration
     * @param {string|object} params.bodyContent - the modal's body content
     * @param {boolean} [params.fullScreen=false] - if modal should be full screen
     * @param {boolean} [params.isDialog=false] - if footer buttons should be added to the modal
     * @param {boolean} [params.showSecondaryButton=true] - if the secondary button should be added to the footer
     * @param {string} [params.type] - predefined headings will be added for types like 'information' or 'warning'
     * @param {string} [params.titleText] - custom heading text
     * @param {string} [params.submitText=Ok] - text on the submit button
     * @param {string} [params.cancelText=Cancel] - text on the cancel button
     * @param {function} [params.submitFunction] - function on submit click, default closes modal
     * @param {function} [params.onCloseCallback] - function on close modal
     * @param {boolean} [params.closeOnBackDropClick=true] - whether the modal should close on backdrop click
     * */
    openModal(params) {
        if (!params.bodyContent) {
            throw new Error('no modal content provided');
        }
        const modal = this.#getModal();
        this.#setDefaultValues(params);
        this.#setClass(params, modal);
        this.#setHeading(params);
        this.#setModalContent(params);
        this.#setFooter(params);
        this.classList.add('show');
        this.onCloseCallback = params?.onCloseCallback;

        if (params.closeOnBackDropClick === false) {
            return;
        }

        this.addEventListener('click', (e) => {
            if (!e.target.closest('[data-modal-content]')) {
                this.closeModal(BkModal.CLOSE_TYPE_X);
            }
        });
    }

    #setClass(params, modal) {
        if (params.fullScreen && !modal.classList.contains('bk-modal--fullscreen')) {
            modal.classList.add('bk-modal--fullscreen');
        } else if (!params.fullScreen) {
            modal.classList.remove('bk-modal--fullscreen');
        }
    }

    #setHeading(params) {
        const titleText = params.type ? this.#getTypeHeading(params.type) : params.titleText;
        if (!titleText) {
            return;
        }
        const heading = document.createElement('h2');
        heading.classList.add('bk-modal__title', 'modal-title');
        heading.id = 'bk-modal-label';
        heading.textContent = titleText;
        this.querySelector('[data-modal-header]')?.prepend(heading);
    }

    #getTypeHeading(type) {
        switch (type) {
            case 'info':
                return t('cs.text.information');
            case 'warning':
                return t('cs.text.warning');
            case 'error':
                return t('sessionTimeoutEnd.title');
            default:
                return '';
        }
    }

    #setModalContent(params) {
        const bodyContainer = document.createElement('div');
        bodyContainer.setAttribute('id', 'bk-modal-body');
        bodyContainer.className = 'bk-modal__body modal-body ';
        if (typeof params.bodyContent === 'string') {
            bodyContainer.innerHTML = params.bodyContent;
        } else if (typeof params.bodyContent === 'object') {
            bodyContainer.appendChild(params.bodyContent);
        }
        this.querySelector('[data-modal-body]').append(bodyContainer);
    }

    #setFooter(params) {
        if (!params.isDialog) {
            return;
        }

        const footer = params.showSecondaryButton
            ? template
                  .getFooterWithTwoButtons({
                      cancelText: params.cancelText,
                      submitText: params.submitText,
                  })
                  .content.cloneNode(true)
            : template.getFooterWithOneButtons({ submitText: params.submitText }).content.cloneNode(true);

        this.querySelector('[data-modal-content]').append(footer);
        this.#setSubmit(params.submitFunction, params.disableSubmitAt);

        if (params.showSecondaryButton) {
            this.querySelector('[data-button-secondary]').addEventListener('click', () => {
                this.closeModal(BkModal.CLOSE_TYPE_SECONDARY_BUTTON);
            });
        }
    }

    #setSubmit(submitFunction, disableSubmitAt) {
        const primaryButton = this.querySelector('[data-button-primary]');
        if (disableSubmitAt) {
            const now = Date.now();
            if (now >= disableSubmitAt) {
                primaryButton.disabled = true;
            } else {
                const timeoutId = setTimeout(() => {
                    primaryButton.disabled = true;
                }, disableSubmitAt - now);
                primaryButton.addEventListener('click', () => {
                    clearTimeout(timeoutId);
                });
            }
        }
        if (submitFunction) {
            primaryButton.addEventListener('click', submitFunction);

            return;
        }
        primaryButton.addEventListener('click', () => {
            this.closeModal(BkModal.CLOSE_TYPE_PRIMARY_BUTTON);
        });
    }

    closeModal(closeType = '') {
        if (this.onCloseCallback instanceof Function) {
            this.onCloseCallback(closeType);
        }
        this.onCloseCallback = null;
        const modal = this.querySelector('[data-page-modal]');
        dispatchStopLoadingEvent(modal);
        this.classList.remove('show');
        this.reset();
    }
}

export default BkModal;
